import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded'
import ConnectedTvOutlinedIcon from '@mui/icons-material/ConnectedTvOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined'
import TelegramIcon from '@mui/icons-material/Telegram'
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded'
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
  Typography
} from '@mui/material'
import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useUserData } from '../../user'
import { useLocalState } from '../../utils/hooks/global/useLocalStorage'

export const SIDEBAR_WIDTH = 248
export const SIDEBAR_COLLAPSED_WIDTH = 72

const MAIN_ITEMS = [
  { title: 'Dashboard', to: '/dashboard', icon: <SpaceDashboardOutlinedIcon /> },
  { title: 'Entities', to: '/entities', icon: <CompareArrowsRoundedIcon /> },
  { title: 'Native Assets', to: '/cnts', icon: <PaidOutlinedIcon /> },
  { title: 'Reporting', to: '/reporting', icon: <SummarizeOutlinedIcon /> },
  { title: 'Hardforks', to: '/hardforks', icon: <UpdateRoundedIcon /> },
  { title: 'Announcements', to: '/announcements', icon: <CampaignOutlinedIcon /> }
]

const ADMIN_ITEMS = [
  { title: 'Users', to: '/admin/users', icon: <GroupOutlinedIcon /> },
  { title: 'Channels', to: '/admin/channels', icon: <ConnectedTvOutlinedIcon /> },
  { title: 'Partners', to: '/admin/partners', icon: <HandshakeOutlinedIcon /> },
  {
    title: 'Bot Settings',
    to: '/admin/partners/bot-settings',
    icon: <TelegramIcon />
  },
  {
    title: 'Status Checks',
    to: '/admin/status-checks',
    icon: <MonitorHeartOutlinedIcon />
  }
]

// The item whose path is the longest prefix of the current path is active,
// so /entities/binance highlights "Entities" and bot settings does not also
// highlight "Partners".
const findActivePath = (pathname, items) =>
  items
    .map((item) => item.to)
    .filter((to) => pathname === to || pathname.startsWith(`${to}/`))
    .sort((a, b) => b.length - a.length)[0]

const NavItem = ({ item, active, collapsed, onNavigate }) => {
  const button = (
    <ListItemButton
      component={Link}
      to={item.to}
      selected={active}
      onClick={onNavigate}
      sx={{
        minHeight: 44,
        borderRadius: '8px',
        mb: 0.5,
        px: collapsed ? 0 : 1.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
        color: '#637381',
        '&:hover': { backgroundColor: '#f4f6f8', color: '#212b36' },
        '&.Mui-selected': {
          backgroundColor: '#e1eaf2',
          color: '#1e3a5f',
          '&:hover': { backgroundColor: '#d5e2ee' }
        }
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 1.5,
          color: 'inherit',
          '& svg': { fontSize: 22 }
        }}
      >
        {item.icon}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.title}
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: active ? 600 : 500
          }}
        />
      )}
    </ListItemButton>
  )

  return collapsed ? (
    <Tooltip title={item.title} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  )
}

const SidebarContent = ({ collapsed, onToggleCollapse, onNavigate }) => {
  const user = useUserData()
  const { pathname } = useLocation()
  const items = user?.isAdmin ? [...MAIN_ITEMS, ...ADMIN_ITEMS] : MAIN_ITEMS
  const activePath = findActivePath(pathname, items)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          height: 64,
          px: collapsed ? 0 : 2.5,
          flexShrink: 0
        }}
      >
        <Box
          component={Link}
          to="/dashboard"
          onClick={onNavigate}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            textDecoration: 'none',
            color: '#212b36'
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/chalmuns-cantina-logo-cropped.png`}
            alt="Chalmuns Cantina"
            style={{ height: 24 }}
          />
          {!collapsed && (
            <Typography
              sx={{ fontWeight: 700, fontSize: 15, letterSpacing: '0.02em' }}
            >
              Chalmuns Cantina
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 1.5 : 2, pt: 1 }}>
        <List disablePadding>
          {MAIN_ITEMS.map((item) => (
            <NavItem
              key={item.to}
              item={item}
              active={item.to === activePath}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </List>

        {user?.isAdmin && (
          <List
            disablePadding
            sx={{ mt: 2 }}
            subheader={
              collapsed ? (
                <Divider sx={{ mb: 1.5 }} />
              ) : (
                <ListSubheader
                  disableSticky
                  sx={{
                    px: 1.5,
                    lineHeight: '32px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#919eab',
                    backgroundColor: 'transparent'
                  }}
                >
                  Admin
                </ListSubheader>
              )
            }
          >
            {ADMIN_ITEMS.map((item) => (
              <NavItem
                key={item.to}
                item={item}
                active={item.to === activePath}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        )}
      </Box>

      {onToggleCollapse && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
            p: 1.5,
            borderTop: '1px solid #e3e5e9',
            flexShrink: 0
          }}
        >
          <Tooltip
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            placement="right"
          >
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  )
}

const paperSx = {
  boxSizing: 'border-box',
  backgroundColor: '#fcfcfc',
  borderRight: '1px solid #e3e5e9'
}

// Permanent sidebar on desktop (collapsible to icons), slide-in drawer on
// phones and tablets.
const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const [collapsed, setCollapsed] = useLocalState(false, 'sidebarCollapsed')
  const { pathname } = useLocation()
  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH

  // Close the mobile drawer after navigating
  useEffect(() => {
    onMobileClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <Box
      component="nav"
      sx={{
        width: { md: width },
        flexShrink: { md: 0 },
        transition: 'width 200ms ease'
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { ...paperSx, width: SIDEBAR_WIDTH }
        }}
      >
        <SidebarContent collapsed={false} onNavigate={onMobileClose} />
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            ...paperSx,
            width,
            overflowX: 'hidden',
            transition: 'width 200ms ease'
          }
        }}
      >
        <SidebarContent
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(!collapsed)}
        />
      </Drawer>
    </Box>
  )
}

export default Sidebar
